import React, { forwardRef, useCallback } from 'react'
import cn from 'classnames'
import { Button as BaseButton } from '@mc/design-system'
import { trackCheckoutStepCompleted } from '@mc/pre-paywall-events'
import { PageLoadEvents } from '@mc/track-event'
import { TrackingContainer } from '@mc/visibility'
import { usePlansContext } from '../../../hooks/usePlansContext'
import { PlansTier } from '../../../types/planProducts'
import { PLANS_PAGE_REFERRER } from '../constants'

export type ButtonProps = {
  ariaLabel: string
  isSticky?: boolean
  text: React.ReactNode
  disabled?: boolean
  onClick: (params: {
    tier: PlansTier
    referrer?: string
    gift?: boolean
  }) => void
  loading?: boolean
  id?: HTMLButtonElement['id']
  onFirstVisible?: () => void
}

const Button = forwardRef(
  (
    {
      ariaLabel,
      isSticky,
      text,
      disabled,
      onClick,
      loading = false,
      onFirstVisible,
      ...props
    }: ButtonProps,
    ref: React.Ref<HTMLButtonElement>,
  ) => {
    const { isGift, selectedPlan } = usePlansContext()

    const handleClick = useCallback(() => {
      if (selectedPlan) {
        trackCheckoutStepCompleted({
          step: 1,
          productName: selectedPlan.product.assetSlug,
          pageSource: PageLoadEvents.PLANS,
        })
        onClick({
          tier: selectedPlan,
          referrer: PLANS_PAGE_REFERRER,
          ...(isGift && { gift: isGift }),
        })
      }
    }, [selectedPlan, isGift, onClick])

    return (
      <TrackingContainer
        onFirstVisible={onFirstVisible}
        className='row no-gutters d-flex justify-content-center'
      >
        <div className='col-12 col-lg-6'>
          <div className={cn('mc-p-2', { 'mc-mt-2': !isSticky })}>
            <BaseButton
              aria-label={ariaLabel}
              ref={ref}
              fullWidth
              onClick={handleClick}
              disabled={!!disabled}
              loading={loading}
              {...props}
            >
              {text}
            </BaseButton>
          </div>
        </div>
      </TrackingContainer>
    )
  },
)
Button.displayName = 'Button'

export { Button }
